import type { GuildScoreSource, Scope } from "./types";

/**
 * Is this board scored by the server rather than by submits? The one predicate
 * the whole feature hangs off — the key picker, the vanished Scoring fields,
 * the locked window, the create payload and the Cycles tab's score-moderation
 * menu all read it rather than comparing the two fields themselves, so a third
 * source would land in one place.
 *
 * Both halves are load-bearing: the source only means anything on
 * scope=guild_aggregated, and a form's draft keeps the source it was given
 * while the operator flips through the scopes.
 *
 * Structural on purpose. The config form asks about the draft it is holding
 * and the Cycles tab about a saved `Leaderboard`; they are different types
 * carrying the same two columns, and the rule they need is one rule. Written
 * once here, it cannot drift into two answers about the same board.
 */
export function isAutoScored(board: {
  scope: Scope;
  guild_score_source?: GuildScoreSource;
}): boolean {
  return (
    board.scope === "guild_aggregated" &&
    board.guild_score_source === "player_stat"
  );
}

/**
 * Is this the team-battle board — the guild's score summed from its
 * participating members' own contributions to the cycle?
 *
 * Written beside `isAutoScored` and shaped exactly like it, because it answers
 * the same question about the same pair of columns: which of the three sources
 * a guild_aggregated board is scored by. Two fields of the config form, the
 * window it may take, the two reward tables and the team preview all read this
 * rather than comparing the columns themselves.
 */
export function isMemberContribution(board: {
  scope: Scope;
  guild_score_source?: GuildScoreSource;
}): boolean {
  return (
    board.scope === "guild_aggregated" &&
    board.guild_score_source === "member_contribution"
  );
}
